/**
 * Sentry initialization for the admin SPA. Mirror of the web app's
 * lib/sentry.ts so admin-side errors (catch blocks, error boundaries,
 * unhandled rejections) land in the same Sentry project, tagged
 * `surface: admin` so they're filterable from the public web surface.
 *
 * No-op when `VITE_SENTRY_DSN` is unset (local dev without DSN
 * configured), so the bundle stays quiet on machines without a
 * project key. Defaults to a low traces sample rate — admin surface
 * has a tiny user count, every span is worth keeping for trace
 * reconstruction.
 *
 * PII redaction matches the web init (`scrubPii` from
 * `@funkihoo/shared`). Email / phone / DOB / home coords never leave
 * the browser unredacted.
 *
 * Env vars read (Vite exposes `VITE_*` to the bundle):
 *   VITE_SENTRY_DSN          required — disables init when missing
 *   VITE_SENTRY_ENVIRONMENT  defaults to VITE_ENV or "development"
 *   VITE_SENTRY_RELEASE      typically the git SHA from CI
 *   VITE_SENTRY_SAMPLE_RATE  float 0..1, defaults to 1.0
 *   VITE_SENTRY_TRACES_SAMPLE_RATE  float 0..1, defaults to 0.5
 */

import { scrubPii } from '@funkihoo/shared';
import * as Sentry from '@sentry/react';

let initialized = false;

export function initAdminSentry(): boolean {
  if (initialized) return true;
  const dsn = import.meta.env['VITE_SENTRY_DSN'];
  if (!dsn) return false;

  Sentry.init({
    dsn,
    environment:
      import.meta.env['VITE_SENTRY_ENVIRONMENT'] ??
      import.meta.env['VITE_ENV'] ??
      (import.meta.env.DEV ? 'development' : 'production'),
    ...(import.meta.env['VITE_SENTRY_RELEASE']
      ? { release: import.meta.env['VITE_SENTRY_RELEASE'] }
      : {}),
    sampleRate: parseFloatEnv('VITE_SENTRY_SAMPLE_RATE', 1.0),
    // Higher than the web default (0.1) — admin has single-digit
    // concurrent users so traces aren't a volume concern, and they're
    // load-bearing when debugging support tooling.
    tracesSampleRate: parseFloatEnv('VITE_SENTRY_TRACES_SAMPLE_RATE', 0.5),
    integrations: [Sentry.browserTracingIntegration()],
    beforeSend(event) {
      return scrubPii(event) as typeof event;
    },
    beforeBreadcrumb(breadcrumb) {
      return scrubPii(breadcrumb) as typeof breadcrumb;
    },
    initialScope: {
      tags: { surface: 'admin' },
    },
  });
  initialized = true;
  return true;
}

/** Re-export the React error boundary so consumers don't reach into
 *  @sentry/react directly — keeps the SDK swap-out surface narrow. */
export const SentryErrorBoundary = Sentry.ErrorBoundary;

export function captureAdminException(err: unknown, context?: Record<string, unknown>): void {
  if (!initialized) return;
  Sentry.captureException(err, context ? { extra: context } : undefined);
}

function parseFloatEnv(name: string, fallback: number): number {
  const raw = import.meta.env[name];
  if (typeof raw !== 'string' || raw.length === 0) return fallback;
  const n = Number.parseFloat(raw);
  return Number.isFinite(n) && n >= 0 && n <= 1 ? n : fallback;
}
